import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { signInWithGooglePopup, getRedirectResult, firebaseSignOut, auth } from '../firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('yegoNewsToken'));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for auth
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check for redirect result on mount (for mobile browsers where popup was blocked)
  useEffect(() => {
    const checkRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          const idToken = await result.user.getIdToken();
          const response = await axios.post(`${BACKEND_URL}/api/auth/firebase-login`, {
            id_token: idToken
          });
          const { access_token, user: userData } = response.data;
          localStorage.setItem('yegoNewsToken', access_token);
          setToken(access_token);
          setUser(userData);
        }
      } catch (error) {
        console.error('Error handling redirect sign-in:', error);
      }
    };
    checkRedirect();
  }, []);

  // Fetch user on mount if token exists
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        localStorage.removeItem('yegoNewsToken');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
      email,
      password
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('yegoNewsToken', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async () => {
    const authResult = await signInWithGooglePopup();
    if (!authResult) {
      // Redirect in progress (popup was blocked)
      return null;
    }
    const { idToken } = authResult;
    const response = await axios.post(`${BACKEND_URL}/api/auth/firebase-login`, {
      id_token: idToken
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('yegoNewsToken', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
      email,
      password,
      name
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('yegoNewsToken', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (e) {
      console.warn("Firebase signout warning:", e);
    }
    localStorage.removeItem('yegoNewsToken');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (updates) => {
    await axios.put(`${BACKEND_URL}/api/auth/profile`, updates);
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      isAdmin: user?.is_admin || user?.role === 'admin' || false,
      isContributor: user?.role === 'contributor' || user?.is_admin || false,
      isSubscriber: user?.role === 'subscriber' || false,
      role: user?.role || (user?.is_admin ? 'admin' : 'subscriber'),
      canWrite: user?.role === 'contributor' || user?.role === 'admin' || user?.is_admin || false,
      login,
      loginWithGoogle,
      register,
      logout,
      updateProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
